<template>
  <m-ais-wrapper class="m-user-stream-wrapper" index="users" sentry-tag="user-stream" sync-store>
    <ais-configure :hits-per-page.camel="12" />
    <div :class="contentClasses">
      <m-ais-stream-container :grid="isGridView">
        <template v-slot:prepend>
          <div class="m-top-section pt-4">
            <div class="m-top-section__left-container">
              <m-ais-search-box />
            </div>
            <div class="m-top-section__right-container">
              <ais-clear-refinements class="filter-toggle" @click.native="toggleFilters">
                <template v-slot="{ canRefine }">
                  <div>
                    <v-icon color="primary">{{ canRefine ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
                    <span>{{ $t('filters.showFilters') }}</span>
                  </div>
                </template>
              </ais-clear-refinements>
              <div class="ml-auto d-flex flex-row">
                <v-btn
                  v-if="showMatchingWizardButton"
                  class="m-top-section__matching-wizard-button mx-2"
                  color="primary"
                  depressed
                  sentry-tag="user-stream.button.matching-wizard"
                  x-large
                  @click="handleMatchingWizard"
                >
                  {{ $t('userStream.matchingWizardBtn') }}
                </v-btn>
                <v-btn
                  v-if="showMatchButton"
                  class="m-top-section__match-button mx-2"
                  color="button"
                  depressed
                  sentry-tag="user-stream.button.match"
                  x-large
                  @click="newMatchesDialog = true"
                >
                  {{ $t('dashboard.matchBtn') }}
                </v-btn>
                <div class="view-toggle">
                  <a sentry-tag="user-stream.button.enableGridView" @click.prevent="enableGridView(true)">
                    <svg
                      :fill="isGridView ? getSelectedIconColor : getUnSelectedIconColor"
                      height="24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g>
                        <path
                          d="M10.5 0H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM23.5 0h-10a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM10.5 13H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5v-10a.5.5 0 0 0-.5-.5Z"
                        />
                        <path
                          d="M10.5 0H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM23.5 13h-10a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5v-10a.5.5 0 0 0-.5-.5Z"
                        />
                      </g>
                    </svg>
                  </a>

                  <a sentry-tag="user-stream.button.disableGridView" @click.prevent="enableGridView(false)">
                    <svg fill="none" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                      <g :fill="!isGridView ? getSelectedIconColor : getUnSelectedIconColor" clip-path="url(#a)">
                        <path
                          d="M23.5 9H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5ZM23.5 1H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5ZM23.5 17H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5Z"
                        />
                      </g>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <m-ais-quick-filters v-if="isQuickFiltersAvailable" @onAllFiltersClicked="toggleFilters" />
        </template>
        <template v-if="isInvitesAvailable && isCommunityEmpty" v-slot:append-hit>
          <m-invite-user-card />
        </template>
        <template v-slot:item="{ item }">
          <m-user-card :row="!isGridView" :user="makeUserEntity(item)" @onUserDetails="openUserDetails(item)" />
        </template>
      </m-ais-stream-container>
      <m-loader v-if="false" :isLoading="isLoading" />
    </div>
    <m-stream-filters v-model="isFiltersVisible" :type="FilterType.PEOPLE" />
    <router-view />
    <m-new-match-dialog v-model="newMatchesDialog" />
    <m-side-panel-dialog v-model="matchingSuggestionsDialog" :close="closeSuggestions" background-image-url="" wide>
      <!-- White background wrapper for wizard/results -->
      <div class="white">
        <!-- Wizard Step -->
        <template v-if="sidePanelStep === 'wizard'">
          <!-- Header with gray background including steps indicator -->
          <div class="pa-6 grey lighten-4">
            <div class="d-flex justify-space-between align-center mb-4">
              <h2>{{ $t('matchingWizard.title') }}</h2>
            </div>
            <!-- Steps indicator in gray section -->
            <div class="step-indicator mb-0">
              <div class="step" :class="{ active: wizardCurrentStep === 1, completed: wizardCurrentStep > 1 }">
                <div class="step-number">1</div>
                <div class="step-label">{{ $t('matchingWizard.steps.themes') }}</div>
              </div>
              <div class="step" :class="{ active: wizardCurrentStep === 2, completed: wizardCurrentStep > 2 }">
                <div class="step-number">2</div>
                <div class="step-label">{{ $t('matchingWizard.steps.preferences') }}</div>
              </div>
              <div class="step" :class="{ active: wizardCurrentStep === 3 }">
                <div class="step-number">3</div>
                <div class="step-label">{{ $t('matchingWizard.steps.availability') }}</div>
              </div>
            </div>
          </div>
          <!-- Wizard content with white background -->
          <div class="pa-6">
            <m-matching-wizard-content
              @wizard-completed="onWizardCompleted"
              @step-changed="wizardCurrentStep = $event"
              :hide-steps="true"
            />
          </div>
        </template>

        <!-- Results Step -->
        <template v-else-if="sidePanelStep === 'results'">
          <!-- Header with gray background -->
          <div class="pa-6 grey lighten-4">
            <div class="d-flex align-center mb-4">
              <v-btn
                icon
                @click="goBackToWizard"
                :title="$t('matchingWizard.backToWizard')"
                class="back-to-wizard-btn mr-3"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <h2 class="mb-0">{{ $t('matchingSuggestions.title') }}</h2>
            </div>
            <p class="mb-0 grey--text">{{ $t('matchingSuggestions.description') }}</p>
          </div>

          <!-- Results content with white background -->
          <div class="pa-6 white">
            <!-- AIS Search Results for Matching Suggestions -->
            <m-ais-wrapper
              v-if="selectedWizardTopics.length > 0"
              class="matching-suggestions-wrapper"
              index="users"
              sentry-tag="matching-suggestions"
            >
              <ais-configure :hits-per-page.camel="10" :query="searchQuery" />
              <m-ais-stream-container :grid="true">
                <template v-slot:item="{ item }">
                  <m-user-card
                    :key="item.objectID"
                    :row="false"
                    :user="makeUserEntity(item)"
                    @onUserDetails="openUserDetails(item)"
                  />
                </template>
              </m-ais-stream-container>
            </m-ais-wrapper>

            <!-- Empty state when no topics selected -->
            <div v-else class="text-center pa-8">
              <v-icon size="64" color="grey lighten-1">mdi-account-multiple</v-icon>
              <h3 class="mt-4 mb-2">{{ $t('matchingSuggestions.noResultsTitle') }}</h3>
              <p class="grey--text">{{ $t('matchingSuggestions.noResults') }}</p>
            </div>
          </div>
        </template>
      </div>
    </m-side-panel-dialog>
    <m-nps-dialog v-model="showNpsDialog" :nps="nps" />
  </m-ais-wrapper>
</template>

<script lang="ts">
import Vue from 'vue';
import { IUserIdentity, UserEntity } from '@mentessa/types';
import { UsersState } from '@/store/users';
import { mapGetters, mapState } from 'vuex';
import { MAisQuickFilters, MAisSearchBox, MAisStreamContainer, MAisWrapper } from '@/components/MAIS';
import MStreamFilters, { FilterType } from '@/components/MStreamFilters';
import { MInviteUserCard, MUserCard } from '@/components/Cards/MUserCard';

import MLoader from '@/components/MLoader.vue';
import { RouteName } from '@/router/types';

import { MNewMatchDialog, MNpsDialog } from '@/components/Dialogs';
import MSidePanelDialog from '@/components/Dialogs/MSidePanelDialog/MSidePanelDialog.vue';
import MMatchingWizardContent from '@/components/Dialogs/MMatchingWizardDialog/MMatchingWizardContent.vue';
import { NpsState } from '@/store/nps';

import { TenantState } from '@/store/tenant';

export default Vue.extend({
  name: 'm-user-stream',
  components: {
    MAisQuickFilters,
    MNpsDialog,
    MNewMatchDialog,
    MMatchingWizardContent,
    MSidePanelDialog,
    MLoader,
    MUserCard,
    MInviteUserCard,
    MAisStreamContainer,
    MStreamFilters,
    MAisWrapper,
    MAisSearchBox,
  },
  props: {
    matchingMode: { type: Boolean, default: false },
  },
  data: () => ({
    view: 'grid',
    isFiltersVisible: false,
    isGridView: true,
    isLoading: false,
    newMatchesDialog: false,
    matchingWizardDialog: false,
    matchingSuggestionsDialog: false,
    selectedWizardTopics: [],
    sidePanelStep: 'wizard', // 'wizard' or 'results'
    wizardCurrentStep: 1,
  }),
  computed: {
    ...mapGetters('tenant', {
      isMatchingAvailable: 'isMatchingAvailable',
      isInvitesAvailable: 'isInvitesAvailable',
      isCommunityEmpty: 'isCommunityEmpty',
      featureConfig: 'featureConfig',
      isQuickFiltersAvailable: 'isQuickFiltersAvailable',
      isMatchingWizardAvailable: 'isMatchingWizardAvailable',
    }),
    ...mapState<TenantState>('tenant', {
      customFields: (state: TenantState) => state.tenant.attributes?.customFields?.map((field) => field.name) ?? [],
    }),
    ...mapState<UsersState>('users', {
      detailedUser: (state: UsersState) => state.detailedUser,
      me: (state: UsersState) => state.me,
    }),
    ...mapState<NpsState>('nps', {
      nps: (state: NpsState) => state.nps,
    }),
    showNpsDialog() {
      return this.nps != null && !this.newMatchesDialog;
    },
    getSelectedIconColor() {
      return this.$vuetify.theme.themes.light.primary;
    },
    getUnSelectedIconColor() {
      return this.$vuetify.theme.themes.light.lightgray;
    },
    showMatchButton() {
      return this.isMatchingAvailable && this.isInvitesAvailable;
    },
    showMatchingWizardButton() {
      return this.isMatchingWizardAvailable;
    },
    contentClasses() {
      return {
        'm-main-container': true,
        'm-main-container--with-filter': this.isFiltersVisible && this.$vuetify.breakpoint.lgAndUp,
      };
    },
    // Expose the FilterType enum to the template
    FilterType() {
      return FilterType;
    },
    // Generate search query from selected wizard topics
    searchQuery() {
      return this.selectedWizardTopics.join(' ');
    },
  },
  methods: {
    async openUserDetails(hit) {
      if (this.$route.name !== RouteName.CommunityUserDetails) {
        await this.$router.push({ name: RouteName.CommunityUserDetails, params: { userId: hit.userId } });
      }
    },

    enableGridView(enabled: boolean) {
      this.isGridView = enabled;
    },

    toggleFilters() {
      this.isFiltersVisible = !this.isFiltersVisible;
    },

    makeUserEntity(searchItem): UserEntity & { highlights: Array<Record<string, unknown>> } {
      const result = {
        id: searchItem.userId,
        tenant: {
          id: searchItem.tenantId,
        },
        attributes: {
          jobTitle: searchItem.jobTitle,
          location: searchItem.location,
          mentoring: {
            available: searchItem.isAvailable ?? false,
          },
          roles: searchItem.roles,
          company: searchItem.company,
        },
        identity: {
          attributes: {
            firstName: searchItem.firstName,
            lastName: searchItem.lastName,
            email: searchItem.email,
            image: searchItem.image,
          },
        } as IUserIdentity,
        expertiseTags: searchItem.eTags.map((tag) => ({ name: tag })),
        interestTags: searchItem.iTags.map((tag) => ({ name: tag })),
        isFavorite: searchItem.isFavorite ?? false,
        highlights: searchItem.highlights,
      };

      if (this.customFields?.length > 0) {
        const custom = {};
        this.customFields.forEach((name: string) => {
          if (name && searchItem[name]) {
            custom[name] = this.$t(`profile.custom.${name}.items.${searchItem[name]}`);
          }
        });
        result.attributes['custom'] = custom;
      }

      return result;
    },
    handleMatchingWizard() {
      // Open the side panel with wizard step
      this.sidePanelStep = 'wizard';
      this.matchingSuggestionsDialog = true;
    },
    onWizardCompleted(wizardData) {
      // Handle the completed wizard data
      console.log('Wizard completed with data:', wizardData);

      // Store the selected topics for filtering
      this.selectedWizardTopics = wizardData.topics || [];
      console.log('Selected wizard topics:', this.selectedWizardTopics);
      console.log('Search query:', this.searchQuery);

      // Here you could dispatch actions to save the wizard results
      // this.$store.dispatch(new SaveMatchingWizardPreferences(wizardData));

      // Switch to results step in the same side panel
      this.sidePanelStep = 'results';
    },
    closeSuggestions() {
      this.matchingSuggestionsDialog = false;
      this.sidePanelStep = 'wizard'; // Reset to wizard for next time
    },
    goBackToWizard() {
      this.sidePanelStep = 'wizard';
    },
  },
});
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_variables';

.m-top-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 10px;

  @media #{map-get($display-breakpoints, 'xs-only')} {
    flex-direction: column;
  }

  .m-top-section__right-container {
    flex: 1;
    display: flex;
  }

  .m-top-section__left-container {
    flex: 1;
  }

  &__match-button {
    height: 52px !important;
    min-width: auto !important;
    font-size: 16px !important;
  }

  &__matching-wizard-button {
    height: 52px !important;
    min-width: auto !important;
    font-size: 16px !important;
  }
}

.filter-toggle {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 51px;
  max-width: 94px;
  border-radius: 6px;
  border: 1px solid var(--v-beige-base);
  box-shadow: 0 0 1px var(--v-beige-base);
  padding: 0 16px;
  cursor: pointer;
  width: 100%;
  background-color: var(--v-white-base);

  span {
    font-size: 14px;
    font-weight: 500;
  }
}

.view-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 51px;
  max-width: 90px;
  border-radius: 6px;
  border: 1px solid var(--v-beige-base);
  padding: 0 16px;
  width: 100%;
  background-color: var(--v-white-base);

  a {
    display: block;
    font-size: 0;
  }

  a + a {
    margin-left: 16px;
  }
}

.m-main-container {
  max-width: 1126px;
  width: 100%;

  &--with-filter {
    margin-right: 320px;
  }
}

.m-user-stream-wrapper {
  width: 100%;
  display: flex;
}

.back-to-wizard-btn {
  z-index: 10;
  pointer-events: auto;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
}

.step-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    position: relative;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 16px;
      right: -50%;
      width: 100%;
      height: 2px;
      background-color: #e0e0e0;
      z-index: 0;
    }

    &.completed::after {
      background-color: var(--v-primary-base);
    }

    .step-number {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: #e0e0e0;
      color: #666;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      margin-bottom: 0.5rem;
      z-index: 1;
      position: relative;
    }

    &.active .step-number {
      background-color: var(--v-primary-base);
      color: white;
    }

    &.completed .step-number {
      background-color: var(--v-primary-base);
      color: white;
    }

    .step-label {
      font-size: 0.875rem;
      text-align: center;
      color: #666;
      z-index: 1;
    }

    &.active .step-label {
      color: var(--v-primary-base);
      font-weight: 500;
    }
  }
}

.white {
  background-color: white !important;
  // min-height: 100vh;
  width: 100%;
  position: relative;
}
</style>
